import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "statusTextarea", "statusUpdatedLabel",
    "loadingSkeleton", "tableWrap", "tableBody", "emptyState",
    "createModal", "createNameInput", "createDateInput",
    "editModal", "editId", "editNameInput", "editDateInput",
    "deleteModal", "deleteId"
  ]

  static values = {
    projectId: Number,
    loadUrl: String,
    updateStatusUrl: String,
    updateKeyDateUrl: String,
    addKeyDateUrl: String,
    destroyKeyDateUrl: String,
    exportUrl: String,
    isOwner: Boolean
  }

  connect() {
    this._statusTimeout = null
    this._loadData()
  }

  async _loadData() {
    try {
      const res = await fetch(this.loadUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      const data = await res.json()

      if (this.hasStatusTextareaTarget) {
        this.statusTextareaTarget.value = data.status || ""
      }
      if (this.hasStatusUpdatedLabelTarget && data.status_updated_by) {
        const dateStr = data.status_updated_in ? new Date(data.status_updated_in).toLocaleDateString() : ""
        this.statusUpdatedLabelTarget.textContent = `Last updated by ${data.status_updated_by}${dateStr ? ` on ${dateStr}` : ""}`
      }

      this._keyDates = data.key_dates || []
      this._renderTable()

      if (this.hasLoadingSkeletonTarget) this.loadingSkeletonTarget.classList.add("hidden")
    } catch (e) {
      console.error("[design-key-dates] Load error:", e)
    }
  }

  // ── Rendering ────────────────────────────────────────────────

  _renderTable() {
    if (!this.hasTableBodyTarget) return

    if (this._keyDates.length === 0) {
      if (this.hasTableWrapTarget) this.tableWrapTarget.classList.add("hidden")
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
      return
    }

    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    if (this.hasTableWrapTarget) this.tableWrapTarget.classList.remove("hidden")
    this.tableBodyTarget.innerHTML = this._keyDates.map((kd, i) => this._rowHtml(kd, i)).join("")
  }

  _rowHtml(kd, index) {
    const dateDisplay = kd.estimated_date ? this._formatDate(kd.estimated_date) : ""
    const stripe = index % 2 === 1 ? "bg-gray-50/60" : "bg-white"
    const kebab = this.isOwnerValue ? `
      <td class="px-3 py-1.5 text-right">
        <div class="relative" data-controller="dropdown">
          <button type="button" class="p-1 rounded hover:bg-gray-100 cursor-pointer" data-action="click->dropdown#toggle">
            <svg class="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M10.5 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Z" clip-rule="evenodd"/></svg>
          </button>
          <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-32" data-dropdown-target="menu">
            <button type="button"
                    class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                    data-action="click->design-key-dates#openEditModal"
                    data-kd-id="${kd.id}" data-kd-name="${this._esc(kd.name || "")}" data-kd-date="${kd.estimated_date || ""}">
              Edit
            </button>
            <button type="button"
                    class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                    data-action="click->design-key-dates#openDeleteModal"
                    data-kd-id="${kd.id}">
              Delete
            </button>
          </div>
        </div>
      </td>
    ` : ""

    return `
      <tr class="${stripe} border-b border-gray-100 hover:bg-brand/[0.04]">
        <td class="px-3 py-1.5 text-[13px] text-gray-900 font-medium">${this._esc(kd.name || "")}</td>
        <td class="px-3 py-1.5 text-[13px] text-gray-600">${dateDisplay}</td>
        ${kebab}
      </tr>
    `
  }

  _formatDate(dateStr) {
    if (!dateStr) return ""
    const d = new Date(dateStr + "T00:00:00")
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
  }

  // ── Status auto-save ───────────────────────────────────────

  debounceSaveStatus() {
    clearTimeout(this._statusTimeout)
    this._statusTimeout = setTimeout(() => this._saveStatus(), 800)
  }

  async _saveStatus() {
    if (!this.hasStatusTextareaTarget) return
    try {
      const res = await this._post(this.updateStatusUrlValue, {
        status: this.statusTextareaTarget.value, project_id: this.projectIdValue
      })
      const data = await res.json()
      if (this.hasStatusUpdatedLabelTarget && data.updated_by) {
        const dateStr = data.updated_in ? new Date(data.updated_in).toLocaleDateString() : ""
        this.statusUpdatedLabelTarget.textContent = `Last updated by ${data.updated_by}${dateStr ? ` on ${dateStr}` : ""}`
      }
    } catch (e) {
      console.error("[design-key-dates] Save status error:", e)
    }
  }

  // ── Create Modal ───────────────────────────────────────────

  openCreateModal() {
    if (this.hasCreateNameInputTarget) this.createNameInputTarget.value = ""
    if (this.hasCreateDateInputTarget) this._clearDatepicker(this.createDateInputTarget)
    if (this.hasCreateModalTarget) this.createModalTarget.classList.remove("hidden")
    setTimeout(() => { if (this.hasCreateNameInputTarget) this.createNameInputTarget.focus() }, 100)
  }

  closeCreateModal() {
    if (this.hasCreateModalTarget) this.createModalTarget.classList.add("hidden")
  }

  async submitCreate() {
    const name = this.hasCreateNameInputTarget ? this.createNameInputTarget.value.trim() : ""
    if (!name) return

    const date = this.hasCreateDateInputTarget ? this.createDateInputTarget.value : ""

    try {
      const res = await this._post(this.addKeyDateUrlValue, {
        name, estimated_date: date || null, project_id: this.projectIdValue
      })
      const kd = await res.json()
      this._keyDates.push(kd)
      this._renderTable()
      this.closeCreateModal()
      this._showToast("Key date added")
    } catch (e) {
      console.error("[design-key-dates] Create error:", e)
    }
  }

  // ── Edit Modal ─────────────────────────────────────────────

  openEditModal(event) {
    const el = event.currentTarget
    if (this.hasEditIdTarget) this.editIdTarget.value = el.dataset.kdId
    if (this.hasEditNameInputTarget) this.editNameInputTarget.value = el.dataset.kdName || ""
    if (this.hasEditDateInputTarget) this._setDatepicker(this.editDateInputTarget, el.dataset.kdDate || "")
    if (this.hasEditModalTarget) this.editModalTarget.classList.remove("hidden")
  }

  closeEditModal() {
    if (this.hasEditModalTarget) this.editModalTarget.classList.add("hidden")
  }

  async submitEdit() {
    const id = this.hasEditIdTarget ? this.editIdTarget.value : ""
    if (!id) return

    const name = this.hasEditNameInputTarget ? this.editNameInputTarget.value.trim() : ""
    if (!name) return

    const date = this.hasEditDateInputTarget ? this.editDateInputTarget.value : ""

    try {
      await this._post(this.updateKeyDateUrlValue, {
        id, name, estimated_date: date || null, project_id: this.projectIdValue
      })
      const kd = this._keyDates.find(k => k.id === parseInt(id))
      if (kd) {
        kd.name = name
        kd.estimated_date = date || null
      }
      this._renderTable()
      this.closeEditModal()
      this._showToast("Key date updated")
    } catch (e) {
      console.error("[design-key-dates] Edit error:", e)
    }
  }

  // ── Delete Modal ───────────────────────────────────────────

  openDeleteModal(event) {
    const id = event.currentTarget.dataset.kdId
    if (this.hasDeleteIdTarget) this.deleteIdTarget.value = id
    if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.remove("hidden")
  }

  closeDeleteModal() {
    if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.add("hidden")
  }

  async submitDelete() {
    const id = this.hasDeleteIdTarget ? this.deleteIdTarget.value : ""
    if (!id) return

    try {
      await fetch(this.destroyKeyDateUrlValue, {
        method: "DELETE",
        headers: this._headers(),
        credentials: "same-origin",
        body: JSON.stringify({ id, project_id: this.projectIdValue })
      })
      this._keyDates = this._keyDates.filter(kd => kd.id !== parseInt(id))
      this._renderTable()
      this.closeDeleteModal()
      this._showToast("Key date deleted")
    } catch (e) {
      console.error("[design-key-dates] Delete error:", e)
    }
  }

  // ── Modal backdrop close ───────────────────────────────────

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) {
      // Close whichever modal is open
      this.closeCreateModal()
      this.closeEditModal()
      this.closeDeleteModal()
    }
  }

  // ── Helpers ────────────────────────────────────────────────

  _post(url, body) {
    return fetch(url, {
      method: "POST",
      headers: this._headers(),
      credentials: "same-origin",
      body: JSON.stringify(body)
    })
  }

  _headers() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
    }
  }

  _clearDatepicker(input) {
    input.value = ""
    if (input._flatpickr) input._flatpickr.clear()
  }

  _setDatepicker(input, dateStr) {
    input.value = dateStr
    if (input._flatpickr) input._flatpickr.setDate(dateStr || null, true)
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
